import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const Reports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const grantReports = [
    {
      title: "Community Foundation Grant Report",
      partner: "Community Foundation of the North State",
      description: "Detailed report documenting the support provided for the launch of Kioo Radio 98.1FM, including activities, outcomes, and stewardship summary.",
      buttonText: "View Report",
      link: "/reports/community-foundation",
      icon: "📊",
      isInternal: true
    },
    {
      title: "YNOP Partnership Report",
      partner: "Your Network of Praise (YNOP)",
      description: "Report outlining YNOP's role in strengthening Christian broadcasting across the Makona River region through Kioo Radio.",
      buttonText: "Open Report Folder",
      link: "#",
      icon: "🤝"
    },
    {
      title: "Galcom Technical Support Report",
      partner: "Galcom International",
      description: "Technical and operational reporting for equipment support, installation progress, and system setup on Betche Hill.",
      buttonText: "View Report",
      link: "#",
      icon: "🔧"
    }
  ];

  const impactReports = [
    {
      title: "Kioo Radio Launch Impact Report – Foya, Liberia",
      description: "Comprehensive overview of our launch activities and initial community impact.",
      buttonText: "Download PDF",
      link: "#",
      icon: "📄"
    },
    {
      title: "Makona River Region Outreach Highlights",
      description: "Photo and video documentation of our community outreach programs.",
      buttonText: "Open Photo & Video Folder",
      link: "#",
      icon: "📸"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Header Section */}
      <section className="bg-gradient-to-r from-kioo-primary to-kioo-secondary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              📋 Reports & Transparency
            </h1>
            <p className="text-xl text-green-100 max-w-4xl mx-auto leading-relaxed">
              Kioo Radio 98.1FM is committed to openness and accountability. This page provides access to 
              narrative impact reports, grant reports, and key documents for partners supporting the work of 
              Kioo Radio across Liberia, Sierra Leone, and Guinea.
            </p>
          </div>
        </div>
      </section>

      {/* Grant Reports Section - PRIORITY */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-kioo-dark mb-4">
              📑 Grant Reports
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Detailed reporting for our valued partners and grant-makers
            </p>
          </div>

          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
            {grantReports.map((report, index) => (
              <div 
                key={index} 
                className="bg-white border-2 border-kioo-primary rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-kioo-primary to-kioo-secondary p-6">
                  <div className="text-4xl mb-3 text-center">{report.icon}</div>
                  <h3 className="text-xl font-bold text-white text-center mb-2">
                    {report.title}
                  </h3>
                  <p className="text-green-100 text-sm text-center font-medium">
                    {report.partner}
                  </p>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <p className="text-gray-700 leading-relaxed mb-6 min-h-[100px]">
                    {report.description}
                  </p>

                  {/* Button */}
                  {report.isInternal ? (
                    <button
                      onClick={() => navigate(report.link)}
                      className="block w-full text-center px-6 py-3 rounded-lg font-semibold transition-all duration-300 bg-kioo-primary text-white hover:bg-kioo-secondary hover:scale-105 shadow-md cursor-pointer"
                    >
                      🔗 {report.buttonText}
                    </button>
                  ) : (
                    <a
                      href={report.link}
                      target={report.link !== "#" ? "_blank" : undefined}
                      rel={report.link !== "#" ? "noopener noreferrer" : undefined}
                      className={`block w-full text-center px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                        report.link !== "#"
                          ? "bg-kioo-primary text-white hover:bg-kioo-secondary hover:scale-105 shadow-md"
                          : "bg-gray-200 text-gray-500 cursor-not-allowed"
                      }`}
                      onClick={(e) => {
                        if (report.link === "#") {
                          e.preventDefault();
                        }
                      }}
                    >
                      {report.link !== "#" ? "🔗 " : "🔒 "}{report.buttonText}
                      {report.link === "#" && (
                        <span className="block text-xs mt-1">(Coming Soon)</span>
                      )}
                    </a>
                  )}
                </div>

                {/* Card Footer Badge */}
                {report.link !== "#" && (
                  <div className="bg-green-50 border-t border-green-100 px-6 py-3">
                    <p className="text-xs text-green-700 text-center font-medium">
                      ✅ Report Available
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Reports Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-kioo-dark mb-4">
              💝 Impact Reports
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Stories and documentation of lives transformed through Kioo Radio
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {impactReports.map((report, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200"
              >
                <div className="p-8">
                  <div className="text-5xl mb-4 text-center">{report.icon}</div>
                  <h3 className="text-xl font-bold text-kioo-dark mb-3 text-center">
                    {report.title}
                  </h3>
                  <p className="text-gray-600 mb-6 text-center leading-relaxed">
                    {report.description}
                  </p>
                  
                  <button
                    disabled
                    className="w-full bg-gray-200 text-gray-500 px-6 py-3 rounded-lg font-semibold cursor-not-allowed"
                  >
                    🔒 {report.buttonText}
                    <span className="block text-xs mt-1">(Coming Soon)</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Summary Placeholder */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8 border-2 border-blue-100 text-center">
            <div className="text-4xl mb-4">💰</div>
            <h2 className="text-2xl lg:text-3xl font-bold text-kioo-dark mb-4">
              Financial Summary (Coming Soon)
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              We are preparing a simple overview of how donated funds are used to support ministry, 
              operations, and community outreach.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-kioo-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">📧 Request More Information</h2>
          <p className="text-xl text-green-100 mb-6 leading-relaxed">
            For specific donor documents or detailed reporting, please contact us at:
          </p>
          <a 
            href="mailto:admin@kiooradio.org"
            className="inline-flex items-center bg-white text-kioo-primary px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
          >
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            admin@kiooradio.org
          </a>

          {/* Additional Contact Options */}
          <div className="mt-8 pt-8 border-t border-green-400">
            <p className="text-green-100 mb-4">Alternative Contact Methods:</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+231887981981"
                className="inline-flex items-center bg-white bg-opacity-10 backdrop-blur-sm border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-kioo-primary transition-all"
              >
                📞 +231 887 981 981
              </a>
              <a 
                href="/contact"
                className="inline-flex items-center bg-white bg-opacity-10 backdrop-blur-sm border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-kioo-primary transition-all"
              >
                💬 Contact Form
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-3xl mb-3">✅</div>
              <h3 className="font-bold text-kioo-dark mb-2">Full Transparency</h3>
              <p className="text-gray-600 text-sm">All reports available to partners</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="font-bold text-kioo-dark mb-2">Regular Updates</h3>
              <p className="text-gray-600 text-sm">Quarterly reporting to stakeholders</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-3xl mb-3">🤝</div>
              <h3 className="font-bold text-kioo-dark mb-2">Stewardship</h3>
              <p className="text-gray-600 text-sm">Faithful management of resources</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Reports;
